import Vue from 'vue'
import Router from 'vue-router'
import store from '../store'
import checkAuth from '../js/checkAuth'

const Index = () =>
  import(/* webpackChunkName: "index" */ '../components/Index.vue')
const Nav = () => import(/* webpackChunkName: "Nav" */ '../components/Nav.vue')
const AdminHeader = () =>
  import(
    /* webpackChunkName: "AdminHeader" */ '../components/admin/AdminHeader.vue'
  )

Vue.use(Router)

const AsyncRoute = (route) => ({
  path: `/${route.path}`,
  name: route.name,
  components: {
    header: Nav,
    content: route.content,
  },
})

const AsyncAuthRoute = (route) => ({
  path: `/admin/${route.path}`,
  name: route.name,
  components: {
    header: AdminHeader,
    content: route.content,
  },
  beforeEnter: (to, from, next) => {
    if (
      store.state &&
      store.state.user &&
      store.state.user.authenticated &&
      store.state.user.is_admin
    ) {
      next()
    } else if (
      localStorage.getItem('id_token') &&
      localStorage.getItem('is_admin')
    ) {
      checkAuth(next)
    } else {
      next('/admin/signin')
    }
  },
})

const router = new Router({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: '/',
      name: 'index',
      components: {
        header: Nav,
        content: Index,
      },
    },
    ...[
      {
        name: 'privacy-policy',
        path: 'privacy-policy',
        content: (resolve) =>
          import('../views/PrivacyPolicy.vue').then(resolve),
      },
      {
        name: 'product-detail',
        path: 'product/:slug',
        content: (resolve) =>
          import('../components/ProductDetail.vue').then(resolve),
      },
      {
        name: 'login',
        path: 'login',
        content: (resolve) => import('../components/Login.vue').then(resolve),
      },
      {
        name: 'search',
        path: 'search/:search',
        content: (resolve) => import('../components/Search.vue').then(resolve),
      },
      {
        name: 'password-email',
        path: 'password/reset',
        content: (resolve) =>
          import('../components/auth/PasswordEmail.vue').then(resolve),
      },
      {
        name: 'cart',
        path: 'cart',
        content: (resolve) => import('../components/Cart.vue').then(resolve),
      },
      {
        name: 'password-email-token',
        path: 'password/reset/:token',
        content: (resolve) =>
          import('../components/auth/PasswordEmailToken.vue').then(resolve),
      },
      {
        name: 'category',
        path: ':category(knihy|hudba|video|kazne)/:page(\\d+)?',
        content: (resolve) =>
          import('../components/ProductList.vue').then(resolve),
      },
    ].map(AsyncRoute),
    {
      name: 'profile-orders',
      path: '/profile/orders',
      meta: {
        requiresAuth: true,
      },
      components: {
        header: Nav,
        content: (resolve) =>
          import('../components/ProfileOrders.vue').then(resolve),
      },
    },
    {
      name: 'profile-account-settings',
      path: '/profile/settings',
      meta: {
        requiresAuth: true,
      },
      components: {
        header: Nav,
        content: (resolve) =>
          import('../components/ProfileAccountSettings.vue').then(resolve),
      },
    },
    ...[
      {
        name: 'admin-dashboard',
        path: '',
        component: 'AdminDashboard',
        content: (resolve) =>
          import('../components/admin/AdminDashboard.vue').then(resolve),
      },
      {
        name: 'admin-product-bundle',
        path: 'bundles',
        component: 'AdminProductBundle',
        content: (resolve) =>
          import('../components/admin/AdminProductBundle.vue').then(resolve),
      },
      {
        name: 'admin-product-list',
        path: 'products',
        component: 'AdminProductList',
        content: (resolve) =>
          import('../components/admin/AdminProductList.vue').then(resolve),
      },
      {
        name: 'admin-product-top',
        path: 'topproducts',
        component: 'AdminProductList',
        content: (resolve) =>
          import('../components/admin/AdminProductList.vue').then(resolve),
      },
      {
        name: 'admin-products-on-page',
        path: 'products/page/:page',
        component: 'AdminProductList',
        content: (resolve) =>
          import('../components/admin/AdminProductList.vue').then(resolve),
      },
      {
        name: 'admin-product-add',
        path: 'product/add',
        component: 'AdminProductAdd',
        content: (resolve) =>
          import('../components/admin/AdminProductAdd.vue').then(resolve),
      },
      {
        name: 'admin-product-edit',
        path: 'product/edit/:idProduct',
        component: 'AdminProductAdd',
        content: (resolve) =>
          import('../components/admin/AdminProductAdd.vue').then(resolve),
      },
      {
        name: 'admin-order-detail',
        path: 'orders/detail/:orderId',
        component: 'AdminOrderDetail',
        content: (resolve) =>
          import('../components/admin/AdminOrderDetail.vue').then(resolve),
      },
      {
        name: 'admin-order-list',
        path: 'orders',
        component: 'AdminOrderList',
        content: (resolve) =>
          import('../components/admin/AdminOrderList.vue').then(resolve),
      },
      {
        name: 'admin-order-list-on-page',
        path: 'orders/page/:page',
        component: 'AdminOrderList',
        content: (resolve) =>
          import('../components/admin/AdminOrderList.vue').then(resolve),
      },
      {
        name: 'admin-customer-detail',
        path: 'customers/detail/:customerId',
        component: 'AdminCustomerDetail',
        content: (resolve) =>
          import('../components/admin/AdminCustomerDetail.vue').then(resolve),
      },
      {
        name: 'admin-customer-list',
        path: 'customers',
        component: 'AdminCustomerList',
        content: (resolve) =>
          import('../components/admin/AdminCustomerList.vue').then(resolve),
      },
      {
        name: 'admin-customer-list-on-page',
        path: 'customers/page/:page',
        component: 'AdminCustomerList',
        content: (resolve) =>
          import('../components/admin/AdminCustomerList.vue').then(resolve),
      },
    ].map(AsyncAuthRoute),
    {
      path: '/admin/signin',
      name: 'admin-signin',
      components: {
        content: (resolve) =>
          import('../components/admin/AdminSignin.vue').then(resolve),
      },
    },
    {
      path: '*',
      components: {
        header: Nav,
        content: (resolve) =>
          import('../components/NotFound.vue').then(resolve),
      },
    },
  ],
})

router.beforeEach((to, from, next) => {
  // Skip router handling for static files in public directory
  if (to.path.startsWith('/public/')) {
    // Force a page reload to serve the static file
    window.location.href = to.path
    return
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (store.state && store.state.user && store.state.user.authenticated) {
      next()
    } else {
      next({
        path: '/',
      })
    }
  } else {
    next()
  }
})

export default router
